
import { useRef, useState } from 'react';
import { Button } from "@/components/ui/button";
import { useToast } from '@/hooks/use-toast';
import { UserRole } from '@/types/user-roles';
import { Link } from 'react-router-dom';
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

interface SignUpFormProps {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  role: UserRole;
  isSubmitting: boolean;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onRoleChange: (value: UserRole) => void;
  onSubmit: (e: React.FormEvent) => void;
}

export const SignUpForm = ({
  firstName,
  lastName,
  email,
  password,
  role,
  isSubmitting,
  onFirstNameChange,
  onLastNameChange,
  onEmailChange,
  onPasswordChange,
  onRoleChange,
  onSubmit,
}: SignUpFormProps) => {
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreedToTerms) {
      return; // Prevent form submission if terms are not accepted
    }
    onSubmit(e);
  };

  return (
    <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
      <div className="rounded-md shadow-sm space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="firstName" className="sr-only">
              First name
            </label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              required
              className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
              placeholder="First name"
              value={firstName}
              onChange={(e) => onFirstNameChange(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="lastName" className="sr-only">
              Last name
            </label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              required
              className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
              placeholder="Last name"
              value={lastName}
              onChange={(e) => onLastNameChange(e.target.value)}
            />
          </div>
        </div>
        <div>
          <label htmlFor="email" className="sr-only">
            Email address
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
            placeholder="Email address"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="password" className="sr-only">
            Password
          </label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="new-password"
            required
            className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
            placeholder="Password"
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
          />
        </div>
        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-700">I am a...</p>
          <RadioGroup
            value={role}
            onValueChange={(value) => onRoleChange(value as UserRole)}
            className="space-y-2"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="regular" id="regular" />
              <Label htmlFor="regular">PEO Shopper</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="broker" id="broker" />
              <Label htmlFor="broker">Broker or Referral Partner</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="peo" id="peo" />
              <Label htmlFor="peo">PEO Provider</Label>
            </div>
          </RadioGroup>
        </div>
        
        <div className="flex items-start mt-4">
          <div className="flex items-center h-5">
            <Checkbox 
              id="terms"
              checked={agreedToTerms}
              onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
              className="h-4 w-4"
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="terms" className="text-gray-600">
              I agree to the <Link to="/terms" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">Terms & Conditions</Link> and <Link to="/privacy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
            </label>
          </div>
        </div>
      </div>

      <div>
        <Button
          type="submit"
          className="w-full"
          disabled={isSubmitting || !agreedToTerms}
        >
          {isSubmitting ? 'Creating account...' : 'Create account'}
        </Button>
      </div>
      
      <p className="text-center text-sm text-gray-600 mt-4">
        Already have an account?{' '}
        <Link
          to="/login"
          className="text-primary hover:text-primary-dark font-medium"
        >
          Sign in
        </Link>
      </p>
    </form>
  );
};
